import { LOGIN_BASIC_CONFIG } from "@/betby/utils/config";

const DEFAULT_STORAGE_KEY_PREFIX = "winburst";
const DEFAULT_BT_RENDERER_SCRIPT_URL =
  "https://ui.invisiblesport.com/bt-renderer.min.js";
const DEFAULT_ANALYTICS_ENDPOINT = "https://api-w-sport.kingsna.com";

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends Record<string, unknown>
    ? DeepPartial<T[K]>
    : T[K];
};

export interface AppConfig {
  api: {
    baseUrl: string;
  };
  analytics: {
    versionName: string;
    versionCode: number;
    productId: string;
    aesKey: string;
    aesIv: string;
    endpoint: string;
  };
  betby: {
    rendererScriptUrl: string;
    rendererScriptUrlUS: string;
  };
  storage: {
    keyPrefix: string;
    deviceIdKey: string;
    keys: {
      nav: string;
      auth: string;
      theme: string;
    };
  };
  auth: {
    loginBasicConfig: typeof LOGIN_BASIC_CONFIG;
  };
}
console.log(
  "env.REACT_APP_BT_RENDERER_SCRIPT_URL",
  process.env.REACT_APP_BT_RENDERER_SCRIPT_URL,
);
export type AppConfigOverrides = DeepPartial<AppConfig>;

function buildStorageKey(prefix: string, suffix: string): string {
  return `${prefix}-${suffix}`;
}

function createStorageConfig(prefix: string): AppConfig["storage"] {
  return {
    keyPrefix: prefix,
    deviceIdKey: buildStorageKey(prefix, "device-id"),
    keys: {
      nav: buildStorageKey(prefix, "nav-storage"),
      auth: buildStorageKey(prefix, "auth-storage"),
      theme: buildStorageKey(prefix, "theme-storage"),
    },
  };
}

export const defaultAppConfig: AppConfig = {
  api: {
    baseUrl: process.env.REACT_APP_API_BASE_URL || "",
  },
  analytics: {
    versionName: process.env.REACT_APP_APP_VERSION ?? "1.0.0",
    versionCode: 100,
    productId: process.env.REACT_APP_ANALYTICS_PRODUCT_ID ?? "2000",
    aesKey: process.env.REACT_APP_ANALYTICS_AES_KEY ?? "7BD2R3s8F2j833jo",
    aesIv: process.env.REACT_APP_ANALYTICS_AES_IV ?? "EzwHiu64Qh989496",
    endpoint:
      process.env.REACT_APP_ANALYTICS_ENDPOINT ?? DEFAULT_ANALYTICS_ENDPOINT,
  },
  betby: {
    rendererScriptUrl:
      process.env.REACT_APP_BT_RENDERER_SCRIPT_URL ??
      DEFAULT_BT_RENDERER_SCRIPT_URL,
    rendererScriptUrlUS:
      process.env.REACT_APP_BT_RENDERER_SCRIPT_URL_US ??
      process.env.REACT_APP_BT_RENDERER_SCRIPT_URL ??
      DEFAULT_BT_RENDERER_SCRIPT_URL,
  },
  storage: createStorageConfig(DEFAULT_STORAGE_KEY_PREFIX),
  auth: {
    loginBasicConfig: LOGIN_BASIC_CONFIG,
  },
};

function resolveStorageConfig(
  storageOverrides?: AppConfigOverrides["storage"],
): AppConfig["storage"] {
  const prefix =
    storageOverrides?.keyPrefix ?? defaultAppConfig.storage.keyPrefix;
  const derivedStorageConfig = createStorageConfig(prefix);

  return {
    keyPrefix: prefix,
    deviceIdKey:
      storageOverrides?.deviceIdKey ?? derivedStorageConfig.deviceIdKey,
    keys: {
      nav: storageOverrides?.keys?.nav ?? derivedStorageConfig.keys.nav,
      auth: storageOverrides?.keys?.auth ?? derivedStorageConfig.keys.auth,
      theme: storageOverrides?.keys?.theme ?? derivedStorageConfig.keys.theme,
    },
  };
}

function resolveLoginBasicConfig(
  authOverrides?: AppConfigOverrides["auth"],
): AppConfig["auth"]["loginBasicConfig"] {
  const loginBasicConfigOverrides = authOverrides?.loginBasicConfig;

  return {
    ab_test:
      loginBasicConfigOverrides?.ab_test ??
      defaultAppConfig.auth.loginBasicConfig.ab_test,
    sdk_version_code:
      loginBasicConfigOverrides?.sdk_version_code ??
      defaultAppConfig.auth.loginBasicConfig.sdk_version_code,
    sdk_version_name:
      loginBasicConfigOverrides?.sdk_version_name ??
      defaultAppConfig.auth.loginBasicConfig.sdk_version_name,
    sn_language:
      loginBasicConfigOverrides?.sn_language ??
      defaultAppConfig.auth.loginBasicConfig.sn_language,
    SDK_ID:
      loginBasicConfigOverrides?.SDK_ID ??
      defaultAppConfig.auth.loginBasicConfig.SDK_ID,
  };
}

export function resolveAppConfig(
  overrides: AppConfigOverrides = {},
): AppConfig {
  return {
    api: {
      baseUrl: overrides.api?.baseUrl ?? defaultAppConfig.api.baseUrl,
    },
    analytics: {
      versionName:
        overrides.analytics?.versionName ??
        defaultAppConfig.analytics.versionName,
      versionCode:
        overrides.analytics?.versionCode ??
        defaultAppConfig.analytics.versionCode,
      productId:
        overrides.analytics?.productId ?? defaultAppConfig.analytics.productId,
      aesKey: overrides.analytics?.aesKey ?? defaultAppConfig.analytics.aesKey,
      aesIv: overrides.analytics?.aesIv ?? defaultAppConfig.analytics.aesIv,
      endpoint:
        overrides.analytics?.endpoint ?? defaultAppConfig.analytics.endpoint,
    },
    betby: {
      rendererScriptUrl:
        overrides.betby?.rendererScriptUrl ??
        defaultAppConfig.betby.rendererScriptUrl,
      rendererScriptUrlUS:
        overrides.betby?.rendererScriptUrlUS ??
        defaultAppConfig.betby.rendererScriptUrlUS,
    },
    storage: resolveStorageConfig(overrides.storage),
    auth: {
      loginBasicConfig: resolveLoginBasicConfig(overrides.auth),
    },
  };
}

let resolvedAppConfig: AppConfig = defaultAppConfig;

const appConfigProxyHandler: ProxyHandler<AppConfig> = {
  get(_target, property) {
    return resolvedAppConfig[property as keyof AppConfig];
  },
  has(_target, property) {
    return property in resolvedAppConfig;
  },
  ownKeys() {
    return Reflect.ownKeys(resolvedAppConfig);
  },
  getOwnPropertyDescriptor(_target, property) {
    const descriptor = Object.getOwnPropertyDescriptor(
      resolvedAppConfig,
      property,
    );
    if (!descriptor) {
      return undefined;
    }

    return {
      ...descriptor,
      configurable: true,
    };
  },
};

export const appConfig = new Proxy({} as AppConfig, appConfigProxyHandler);

export function registerAppConfig(
  overrides: AppConfigOverrides = {},
): AppConfig {
  resolvedAppConfig = resolveAppConfig(overrides);
  return resolvedAppConfig;
}

export function getAppConfig(): AppConfig {
  return resolvedAppConfig;
}

export function resetAppConfig(): AppConfig {
  resolvedAppConfig = defaultAppConfig;
  return resolvedAppConfig;
}
